import React, { useEffect, useMemo, useState } from 'react';
import { Button, Tabs, Tooltip } from 'antd';
import { FiArrowUpLeft, FiMinus } from 'react-icons/fi';
import { CornersInIcon, CornersOutIcon } from '@phosphor-icons/react';
import { PopoutWindowProps } from '../../../../@types/types';
import { useViewerStore } from '../../../../store/store';

const PopoutWindow: React.FC<PopoutWindowProps> = ({
    children,
    className = '',
    panes,
    isOpenByDefault = true,
    iconForButton,
    tooltip,
    isTabBasedContent = true,
    tabTitle,
    buttonID,
    isMaximizedByDefault = false,
}) => {
    const [isMaximized, setIsMaximized] = useState(isMaximizedByDefault);
    const [expand, setExpand] = useState(isOpenByDefault);
    const resolvePaneTitle = (label: React.ReactNode): string => {
        return typeof label === 'string' ? label : '';
    };

    const firstPaneTitle = useMemo(
        () => (isTabBasedContent && panes.length > 0 ? resolvePaneTitle(panes[0].tab) : ''),
        [isTabBasedContent, panes.length, panes[0]?.tab]
    );
    const [activePaneTitle, setActivePaneTitle] = useState<string>(firstPaneTitle);

    const toggleMaximize = () => {
        setIsMaximized((prev) => !prev);
        // Keep popup visible while toggling maximize/minimize states.
        setExpand(true);
    };

    const handleCollapse = () => {
        setIsMaximized(false);
        setExpand(false);
    };

    const handleShow = () => {
        if (isMaximizedByDefault) {
            setIsMaximized(true);
        } else {
            setExpand(true);
        }
    };

    const items = panes.map((pane) => ({
        label: pane.tab,
        key: pane.key,
        children: <div className="popup__tab-pane">{pane.content}</div>,
    }));

    useEffect(() => {
        setActivePaneTitle(firstPaneTitle);
    }, [firstPaneTitle]);

    useEffect(() => {
        //call the onMinMaxPopoutCallback function from the panes array
        const pane = panes.find(p => p.onMinMaxPopoutCallback);
        if (pane && expand) {
            pane.onMinMaxPopoutCallback?.();
        }
    }, [expand]);

    useEffect(() => {
        const activeTitle = isTabBasedContent && activePaneTitle ? activePaneTitle : tabTitle;
        const nextLabel = activeTitle || 'Popout';
        if (useViewerStore.getState().mobilePopoutTabLabel !== nextLabel) {
            useViewerStore.setState({ mobilePopoutTabLabel: nextLabel });
        }
    }, [activePaneTitle, isTabBasedContent, tabTitle]);

    useEffect(() => {
        const body = document.body;
        const isMobileOrTabletViewport = window.innerWidth <= 1024;
        body.classList.toggle('mobile-popout-maximized', isMobileOrTabletViewport && isMaximized);
        return () => {
            body.classList.remove('mobile-popout-maximized');
        };
    }, [isMaximized]);

    return (
        <>
            {tooltip ? (
                <Tooltip placement="left" title={tooltip}>
                    <Button
                        id={buttonID}
                        type="default"
                        size="large"
                        className={`float_button ${expand ? 'hide' : ''}`}
                        onClick={handleShow}
                        icon={iconForButton ? iconForButton : <FiArrowUpLeft size={28} />}
                    />
                </Tooltip>
            ) : (
                <Button
                    type="default"
                    size="large"
                    className={`float_button ${expand ? 'hide' : ''}`}
                    onClick={handleShow}
                    icon={iconForButton ? iconForButton : <FiArrowUpLeft size={28} />}
                />
            )}

            {/* <Collapse accordion={false} expandIconPosition="end"> */}
                <div
                    className={`popup ${className} ${expand && 'expand'} ${isMaximized && 'maximized'} ${!isMaximized && !expand && 'collapse'}`}
                >
                    <div className="top_bar">
                        <b className="popup__header-title" title={tabTitle}>
                            {tabTitle}
                        </b>
                        <div className="popup__header-actions" style={{ display: 'flex', gap: 4 }}>
                            <Button
                                type="text"
                                title="Minimize"
                                className="popup__close popup__close--minimize"
                                onClick={handleCollapse}
                            >
                                <FiMinus size={18} color="#fcfcfc" />
                            </Button>
                            <Button
                                type="text"
                                title={isMaximized ? 'Restore' : 'Maximize'}
                                className="max_min popup__maximize-btn"
                                onClick={toggleMaximize}
                            >
                                {isMaximized ? (
                                    <CornersInIcon size={24} color="#fcfcfc" />
                                ) : (
                                    <CornersOutIcon size={32} color="#fcfcfc" />
                                )}
                            </Button>
                        </div>
                    </div>
                    <div className={`popup__content ${!isMaximized && !expand && 'close'}`}>
                        {
                            isTabBasedContent ? (
                                <Tabs
                                    onChange={(key) => {
                                      //fire the callback function from item.onPaneChangeCallback
                                        const pane = panes.find(p => p.key === key);
                                        if (pane?.tab) {
                                            setActivePaneTitle(resolvePaneTitle(pane.tab));
                                        }
                                        if (pane?.onPaneChangeCallback) {
                                            pane.onPaneChangeCallback();
                                        }
                                    }}
                                    defaultActiveKey="1" items={items} />
                            ) : (
                                <div className="popup__content__container">
                                    {children}
                                </div>
                            )
                        }
                    </div>
                </div>
            {/* </Collapse> */}
        </>
    );
};

export default PopoutWindow;
